import { post } from '../utils/httpRequest';
import {
  buildClickRequest,
  buildDisplayedRequest,
  buildPageLoadedRequest,
} from './request';
import { buildResponse } from './response';
import { checkInitialized, init, getContext } from './init';
import {
  ERROR_COMPONENT_ID_MANDATORY,
  ERROR_COMPONENT_DOM_MANDATORY,
  ERROR_EVENT_MANDATORY,
} from './messages';

import {
  HTML_EVENTS_API_ROUTE,
  CLICK,
  PAGE_LOADED,
  COMPONENT_DISPLAYED,
} from './urls';
import {
  getPageRenderId as getLegacyPageRenderId,
  refreshPageRenderId,
} from './pageRenderID';
import { getPageRenderId } from './standalone';
import {
  setTransactionBookingId as setBookingId,
  setTransactionSearchId as setSearchId,
  setTransactionMembershipId as setMembershipId,
} from './transactionID';
import { createDataMeshTrackingClient } from './dataMesh';
import {
  DeprecatedDatameshEventsTrackerArgs,
  PageLoadedHtmlDatameshEvent,
  PageLoadedTracker,
} from './types';
import { createConfigParametersRepository } from '../configParametersRepository';
import { handleMktSessionError } from './error';

const trackComponentEvent = async (path, id) => {
  if (!id) {
    return Promise.reject({ error: ERROR_COMPONENT_ID_MANDATORY });
  }
  const pageRenderId = getLegacyPageRenderId();
  const request =
    path === CLICK
      ? buildClickRequest(id, pageRenderId)
      : buildDisplayedRequest(id, pageRenderId);

  return post(`${getContext()}${HTML_EVENTS_API_ROUTE}${path}`, request).then(
    () => buildResponse(request)
  );
};

const trackPageLoadedEvent = async (
  mfe
): Promise<{ event: PageLoadedHtmlDatameshEvent }> => {
  const previousPageRenderId = getLegacyPageRenderId();
  const pageRenderId = refreshPageRenderId();
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const mktSessionId = storageClient?.getCookie('mktSession') || undefined;
  if (!mktSessionId) {
    handleMktSessionError(mktSessionId);
  }
  const request = buildPageLoadedRequest(
    mktSessionId,
    previousPageRenderId,
    pageRenderId,
    mfe
  );

  return post(
    `${getContext()}${HTML_EVENTS_API_ROUTE}${PAGE_LOADED}`,
    request
  ).then(() => buildResponse(request));
};

// @deprecated
const trackClick = async ({ id }: DeprecatedDatameshEventsTrackerArgs = {}) => {
  if (!id) {
    return Promise.reject({ error: ERROR_COMPONENT_ID_MANDATORY });
  }
  const pageRenderId = getLegacyPageRenderId();
  const request = buildClickRequest(id, pageRenderId);
  return (async () => {
    await checkInitialized();
    return post(
      `${getContext()}${HTML_EVENTS_API_ROUTE}${CLICK}`,
      request
    ).then(() => buildResponse(request));
  })();
};

// @deprecated
const trackComponentDisplayed = async ({
  id,
}: DeprecatedDatameshEventsTrackerArgs = {}) => {
  await checkInitialized();
  return await trackComponentEvent(COMPONENT_DISPLAYED, id);
};

const getDOMId = (htmlElement) => htmlElement.dataset['id'];

const trackDOMComponentDisplayed = async (htmlElement) => {
  await checkInitialized();
  if (htmlElement instanceof Element && getDOMId(htmlElement)) {
    return await trackComponentEvent(
      COMPONENT_DISPLAYED,
      getDOMId(htmlElement)
    );
  } else {
    return Promise.reject({ error: ERROR_COMPONENT_DOM_MANDATORY });
  }
};

const trackClickEvent = async (event) => {
  await checkInitialized();
  if (event && event.currentTarget && getDOMId(event.currentTarget)) {
    return await trackComponentEvent(CLICK, getDOMId(event.currentTarget));
  } else {
    return Promise.reject({ error: ERROR_EVENT_MANDATORY });
  }
};

const trackPageLoaded: PageLoadedTracker = async ({ mfe } = {}) => {
  await checkInitialized();
  return await trackPageLoadedEvent(mfe);
};

export {
  trackClick,
  trackComponentDisplayed,
  trackPageLoaded,
  trackDOMComponentDisplayed,
  trackClickEvent,
  init,
  setBookingId,
  setSearchId,
  setMembershipId,
  createDataMeshTrackingClient,
  getPageRenderId,
};
